/**
 * Categories API
 *
 * API functions for managing contractor categories
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';

export interface Category {
  id: string;
  slug: string;
  name?: string; // Legacy field, may be returned by some endpoints
  nameEn: string;
  nameFr: string;
  descriptionEn: string | null;
  descriptionFr: string | null;
  icon: string | null;
  parentId: string | null;
  isActive: boolean;
  isPopular: boolean;
  sortOrder: number;
}

export interface ContractorCategory {
  id: string;
  categoryId: string;
  category: {
    id: string;
    nameEn: string;
    nameFr: string;
    slug: string;
    icon: string | null;
  };
}

export interface AssignCategoriesDto {
  categoryIds: string[];
}

/**
 * Get all available categories
 */
export async function getCategories(): Promise<Category[]> {
  const url = buildApiUrl('/categories/public');

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    credentials: 'include',
  });

  return handleApiResponse<Category[]>(response);
}

/**
 * Get category tree
 */
export async function getCategoryTree(): Promise<Category[]> {
  const url = buildApiUrl('/categories/tree');

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    credentials: 'include',
  });

  return handleApiResponse<Category[]>(response);
}

/**
 * Get my assigned categories
 */
export async function getMyCategories(): Promise<ContractorCategory[]> {
  const url = buildApiUrl('/contractors/me/categories');

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    credentials: 'include',
  });

  return handleApiResponse<ContractorCategory[]>(response);
}

/**
 * Assign categories to contractor (max 5)
 */
export async function assignCategories(
  dto: AssignCategoriesDto
): Promise<ContractorCategory[]> {
  const url = buildApiUrl('/contractors/me/categories');

  const response = await fetchWithTokenRefresh(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(dto),
  });

  return handleApiResponse<ContractorCategory[]>(response);
}

/**
 * Remove category from contractor
 */
export async function removeCategory(categoryId: string): Promise<void> {
  const url = buildApiUrl(`/contractors/me/categories/${categoryId}`);

  const response = await fetchWithTokenRefresh(url, {
    method: 'DELETE',
    credentials: 'include',
  });

  await handleApiResponse<void>(response);
}
